import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../../api/ApiService';
import AdminSectionNav from '../../components/AdminSectionNav/AdminSectionNav';
import { useAuth } from '../../components/auth/AuthContext';
import './AdminCanvasProjectsPage.css';

const PAGE_SIZE = 30;

const getProjectId = (project) => project?.uuid || project?.id;

const formatTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('zh-CN', { hour12: false });
};

const extractErrorMessage = (error, fallback) => {
  const detail = error?.data?.detail || error?.data?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return error?.msg || error?.message || fallback;
};

function AdminCanvasProjectsPage() {
  const { user } = useAuth();
  const apiService = useMemo(() => new ApiService(), []);
  const requestVersion = useRef(0);
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [keywordDraft, setKeywordDraft] = useState('');
  const [filters, setFilters] = useState({
    keyword: '',
    visibility: 'all',
    priority_status: 'all'
  });
  const [priorityDrafts, setPriorityDrafts] = useState({});
  const [savingProjectId, setSavingProjectId] = useState(null);
  const [visibilityDialog, setVisibilityDialog] = useState(null);
  const [visibilityReason, setVisibilityReason] = useState('');

  const loadProjects = useCallback(async () => {
    if (!user?.token) return;
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    setLoading(true);
    setError('');
    try {
      const response = await apiService.adminListDrawProjects(user.token, {
        page,
        page_size: PAGE_SIZE,
        ...filters
      });
      if (requestVersion.current !== version) return;
      const nextProjects = Array.isArray(response?.projects) ? response.projects : [];
      setProjects(nextProjects);
      setTotal(Number(response?.total) || 0);
      setTotalPages(Number(response?.total_pages) || 0);
      setPriorityDrafts(Object.fromEntries(
        nextProjects.map((project) => [getProjectId(project), String(project.sort || 0)])
      ));
    } catch (loadError) {
      if (requestVersion.current !== version) return;
      setError(extractErrorMessage(loadError, '画布列表加载失败'));
      setProjects([]);
    } finally {
      if (requestVersion.current === version) setLoading(false);
    }
  }, [apiService, filters, page, user?.token]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setFilters((current) => ({ ...current, keyword: keywordDraft.trim() }));
  };

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handlePrioritySave = async (project, sortOverride = null) => {
    const projectId = getProjectId(project);
    const nextSort = sortOverride === null
      ? Number(priorityDrafts[projectId])
      : Number(sortOverride);
    if (!Number.isInteger(nextSort) || nextSort < -100000 || nextSort > 100000) {
      toast.error('优先级必须是 -100000 到 100000 之间的整数');
      return;
    }
    if (nextSort === Number(project.sort || 0)) return;

    setSavingProjectId(projectId);
    try {
      await apiService.adminUpdateDrawProjectPriority(
        user.token,
        projectId,
        nextSort,
        Number(project.sort || 0)
      );
      toast.success('首页优先级已更新');
      await loadProjects();
    } catch (saveError) {
      toast.error(extractErrorMessage(saveError, '优先级更新失败'));
      await loadProjects();
    } finally {
      setSavingProjectId(null);
    }
  };

  const openVisibilityDialog = (project, action) => {
    setVisibilityDialog({ project, action });
    setVisibilityReason(action === 'disable_public' ? '不符合要求' : '');
  };

  const handleVisibilitySave = async () => {
    if (!visibilityDialog) return;
    const { project, action } = visibilityDialog;
    const reason = visibilityReason.trim();
    if (action === 'disable_public' && !reason) {
      toast.error('请填写关闭公开的原因');
      return;
    }

    const projectId = getProjectId(project);
    setSavingProjectId(projectId);
    try {
      await apiService.adminUpdateDrawProjectVisibility(
        user.token,
        projectId,
        action,
        reason
      );
      toast.success(action === 'disable_public' ? '已关闭项目公开' : '已恢复公开权限');
      setVisibilityDialog(null);
      setVisibilityReason('');
      await loadProjects();
    } catch (saveError) {
      toast.error(extractErrorMessage(saveError, '公开状态更新失败'));
    } finally {
      setSavingProjectId(null);
    }
  };

  return (
    <main className="admin-canvas-projects-page">
      <header className="admin-canvas-projects-header">
        <div>
          <span>CANVAS ADMIN</span>
          <h1>画布管理</h1>
          <p>查看所有用户画布，管理灵感广场优先级与公开状态。</p>
        </div>
        <Link to="/">返回灵感画布</Link>
      </header>

      <AdminSectionNav />

      <form className="admin-canvas-projects-filters" onSubmit={handleSearch}>
        <label>
          <span>搜索项目、UUID、用户</span>
          <input
            type="search"
            value={keywordDraft}
            placeholder="输入项目名、完整 UUID、用户名或用户 ID"
            onChange={(event) => setKeywordDraft(event.target.value)}
          />
        </label>
        <label>
          <span>公开状态</span>
          <select
            value={filters.visibility}
            onChange={(event) => updateFilter('visibility', event.target.value)}
          >
            <option value="all">全部</option>
            <option value="public">公开展示中</option>
            <option value="private">用户私有</option>
            <option value="admin_disabled">管理员已下架</option>
          </select>
        </label>
        <label>
          <span>首页优先级</span>
          <select
            value={filters.priority_status}
            onChange={(event) => updateFilter('priority_status', event.target.value)}
          >
            <option value="all">全部</option>
            <option value="ranked">已设置</option>
            <option value="unranked">未设置</option>
          </select>
        </label>
        <button type="submit">搜索</button>
      </form>

      <section className="admin-canvas-projects-panel" aria-label="所有用户画布列表">
        <div className="admin-canvas-projects-summary">
          <strong>{total.toLocaleString('zh-CN')} 个画布</strong>
          <span>数字越大，在灵感广场越靠前</span>
        </div>

        {error && (
          <div className="admin-canvas-projects-message is-error" role="alert">
            <span>{error}</span>
            <button type="button" onClick={loadProjects}>重试</button>
          </div>
        )}
        {loading && <div className="admin-canvas-projects-message" role="status">正在读取画布列表…</div>}
        {!loading && !error && projects.length === 0 && (
          <div className="admin-canvas-projects-message">没有符合当前条件的画布</div>
        )}

        {!loading && projects.length > 0 && (
          <div className="admin-canvas-projects-table-wrap">
            <table className="admin-canvas-projects-table">
              <thead>
                <tr>
                  <th>画布</th>
                  <th>所有者</th>
                  <th>公开状态</th>
                  <th>首页优先级</th>
                  <th>更新时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => {
                  const projectId = getProjectId(project);
                  const saving = savingProjectId === projectId;
                  return (
                    <tr key={projectId}>
                      <td>
                        <div className="admin-canvas-projects-project">
                          <div className="admin-canvas-projects-thumbnail">
                            {project.thumbnail_url
                              ? <img src={project.thumbnail_url} alt="" loading="lazy" />
                              : <span>无封面</span>}
                          </div>
                          <div>
                            <strong>{project.name || '未命名'}</strong>
                            <small title={project.uuid}>{project.uuid}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <strong>{project.user_name || `用户 ${project.user_id}`}</strong>
                        <small>ID {project.user_id}</small>
                      </td>
                      <td>
                        {project.admin_public_disabled ? (
                          <span className="admin-canvas-projects-status is-disabled">管理员已下架</span>
                        ) : project.is_public ? (
                          <span className="admin-canvas-projects-status is-public">公开</span>
                        ) : (
                          <span className="admin-canvas-projects-status">私有</span>
                        )}
                        {project.admin_public_disabled_reason && (
                          <small title={project.admin_public_disabled_reason}>
                            {project.admin_public_disabled_reason}
                          </small>
                        )}
                      </td>
                      <td>
                        <div className="admin-canvas-projects-priority">
                          <input
                            type="number"
                            min="-100000"
                            max="100000"
                            step="1"
                            aria-label={`${project.name} 首页优先级`}
                            value={priorityDrafts[projectId] ?? String(project.sort || 0)}
                            disabled={saving}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setPriorityDrafts((current) => ({
                                ...current,
                                [projectId]: nextValue
                              }));
                            }}
                          />
                          <button type="button" disabled={saving} onClick={() => handlePrioritySave(project)}>
                            {saving ? '保存中' : '保存'}
                          </button>
                          {Number(project.sort) !== 0 && (
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => handlePrioritySave(project, 0)}
                            >
                              归零
                            </button>
                          )}
                        </div>
                        {!project.is_effectively_public && <small>当前不在灵感广场展示</small>}
                      </td>
                      <td><time dateTime={project.updated_at || ''}>{formatTime(project.updated_at)}</time></td>
                      <td>
                        <div className="admin-canvas-projects-actions">
                          {project.is_effectively_public && (
                            <Link to={`/canvas-editor/${projectId}`}>打开画布</Link>
                          )}
                          {project.admin_public_disabled ? (
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => openVisibilityDialog(project, 'restore_public')}
                            >
                              恢复公开权限
                            </button>
                          ) : project.is_public ? (
                            <button
                              className="is-danger"
                              type="button"
                              disabled={saving}
                              onClick={() => openVisibilityDialog(project, 'disable_public')}
                            >
                              关闭公开
                            </button>
                          ) : <small>用户设为私有</small>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <nav className="admin-canvas-projects-pagination" aria-label="画布列表分页">
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>
              上一页
            </button>
            <span>第 {page} / {totalPages} 页</span>
            <button type="button" disabled={page >= totalPages || loading} onClick={() => setPage((value) => value + 1)}>
              下一页
            </button>
          </nav>
        )}
      </section>

      {visibilityDialog && (
        <div className="admin-canvas-projects-dialog-backdrop" onMouseDown={() => setVisibilityDialog(null)}>
          <section
            className="admin-canvas-projects-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={visibilityDialog.action === 'disable_public' ? '关闭画布公开' : '恢复画布公开权限'}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <h2>{visibilityDialog.action === 'disable_public' ? '关闭画布公开' : '恢复公开权限'}</h2>
            <p>
              {visibilityDialog.action === 'disable_public'
                ? `“${visibilityDialog.project.name}”将立即从灵感广场移除，优先级同时归零。`
                : `将解除“${visibilityDialog.project.name}”的管理员下架限制。`}
            </p>
            {visibilityDialog.action === 'disable_public' && (
              <label>
                <span>关闭原因 *</span>
                <textarea
                  rows="4"
                  maxLength="255"
                  value={visibilityReason}
                  placeholder="填写给项目所有者查看的原因"
                  onChange={(event) => setVisibilityReason(event.target.value)}
                  autoFocus
                />
              </label>
            )}
            <div>
              <button type="button" onClick={() => setVisibilityDialog(null)}>取消</button>
              <button
                className={visibilityDialog.action === 'disable_public' ? 'is-danger' : ''}
                type="button"
                onClick={handleVisibilitySave}
              >
                确认{visibilityDialog.action === 'disable_public' ? '关闭公开' : '恢复'}
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

export default AdminCanvasProjectsPage;
